import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, useTheme } from '@mui/material';
import { IconInfoCircle, IconTag } from '@tabler/icons-react';

import { Chip, Tooltip, Typography } from '@zendesk/forethought-elements';
import { getResolutionTierLabel } from '../../constants';
import { formatTimestamp } from '../../helpers';
import { CopyableId } from './CopyableId';
import { useIsResolutionUiEnabled } from 'src/components/dashboard-pages/solve-insights/common/ResolutionInsightsCards';
import {
  channelToDisplayName,
  ConversationChannel,
} from 'src/types/workflowBuilderAPITypes';

const Row = ({
  children,
  label,
  tooltip,
}: {
  children: ReactNode;
  label: string;
  tooltip?: string;
}) => {
  const { palette } = useTheme();

  return (
    <>
      <Box sx={{ alignItems: 'center', display: 'flex', gap: '4px' }}>
        <Typography color={palette.colors.grey[600]} variant='font12Medium'>
          {label}
        </Typography>
        {tooltip && (
          <Tooltip tooltipContent={tooltip}>
            <IconInfoCircle color={palette.colors.grey[500]} size={13} />
          </Tooltip>
        )}
      </Box>
      <Box sx={{ alignItems: 'center', display: 'flex', minWidth: 0 }}>
        {children}
      </Box>
    </>
  );
};

const Value = ({ children }: { children: ReactNode }) => (
  <Typography variant='font12Medium'>{children}</Typography>
);

// The identifiers an investigator copies into a ticket or a logs query, plus the
// outcome terms the org's contract entitles it to see. Resolutions are gated on
// the same flag the rest of the resolutions UI uses, so a deflection-contract
// org never sees a term it doesn't pay for.
export const IdentityGrid = ({
  channel,
  conversationId,
  deflected,
  resolutionTier,
  startDatetime,
  workflowTag,
}: {
  channel?: string | null;
  conversationId: string;
  deflected?: boolean | null;
  resolutionTier?: string | null;
  startDatetime?: string;
  workflowTag?: string | null;
}) => {
  const { t } = useTranslation('intent-conversation-analytics');
  const { palette } = useTheme();
  const isResolutionUiEnabled = useIsResolutionUiEnabled();

  return (
    <Box
      sx={{
        alignItems: 'center',
        columnGap: '12px',
        display: 'grid',
        gridTemplateColumns: 'max-content 1fr',
        rowGap: '6px',
      }}
    >
      <Row label={t('drawerBody.chatIdLabel')}>
        <CopyableId
          ariaLabel={t('conversationAudit.copyChatId')}
          value={conversationId}
        />
      </Row>
      {channel && (
        <Row label={t('revampDrawerBody.channelLabel')}>
          <Value>
            {channelToDisplayName[channel as ConversationChannel] ?? channel}
          </Value>
        </Row>
      )}
      {startDatetime && (
        <Row label={t('conversationsCard.timeCreatedLabel')}>
          <Value>{formatTimestamp(startDatetime + 'Z')}</Value>
        </Row>
      )}
      {workflowTag && (
        <Row label={t('drawerBody.workflowTagLabel')}>
          <Tooltip placement='top' tooltipContent={workflowTag}>
            <Chip
              backgroundColor={palette.colors.slate[100]}
              color={palette.colors.black}
              label={workflowTag}
              size='small'
              startAdornment={<IconTag size={16} />}
              sx={{
                '& .MuiChip-icon': { color: palette.colors.blue[500] },
                borderRadius: '4px',
                maxWidth: '100%',
                typography: 'font12',
              }}
              variant='filled'
            />
          </Tooltip>
        </Row>
      )}
      {typeof deflected === 'boolean' && (
        <Row
          label={t('drawerBody.deflectedLabel')}
          tooltip={t('conversationAudit.deflectedTooltip')}
        >
          <Value>
            {deflected
              ? t('drawerBody.deflectedTrue')
              : t('drawerBody.deflectedFalse')}
          </Value>
        </Row>
      )}
      {isResolutionUiEnabled && (
        <Row
          label={t('conversationAudit.resolved')}
          tooltip={t('conversationAudit.resolvedTooltip')}
        >
          <Value>{getResolutionTierLabel(resolutionTier)}</Value>
        </Row>
      )}
    </Box>
  );
};
